import { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';

interface SyncPollIndicatorProps {
  /** Current poll interval in ms */
  pollInterval: number;
  /** Called when user clicks — checks now and restarts polling */
  onCheckNow: () => Promise<void>;
  /** Whether syncing is active (controls visibility) */
  active: boolean;
  /** Whether polling has expired (hit the max duration) */
  expired?: boolean;
}

const SIZE = 20;
const STROKE_WIDTH = 2;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export function SyncPollIndicator({ pollInterval, onCheckNow, active, expired }: SyncPollIndicatorProps) {
  const [elapsed, setElapsed] = useState(0);
  const [checking, setChecking] = useState(false);
  const [cooldown, setCooldown] = useState(false);
  const startTimeRef = useRef(Date.now());
  const frameRef = useRef<number>(0);
  const CLICK_COOLDOWN = 5000;

  // Reset countdown whenever pollInterval changes (new poll cycle started)
  useEffect(() => {
    startTimeRef.current = Date.now();
    setElapsed(0);
  }, [pollInterval, active]);

  // Animate the countdown ring
  useEffect(() => {
    if (!active || checking || expired) {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      return;
    }

    const tick = () => {
      const now = Date.now();
      setElapsed(now - startTimeRef.current);
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [active, checking, expired]);

  const handleClick = useCallback(async () => {
    if (checking || cooldown) return;
    setChecking(true);
    setCooldown(true);
    try {
      await onCheckNow();
    } finally {
      setChecking(false);
      startTimeRef.current = Date.now();
      setElapsed(0);
      setTimeout(() => setCooldown(false), CLICK_COOLDOWN);
    }
  }, [checking, cooldown, onCheckNow]);

  if (!active) return null;

  if (expired) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => onCheckNow()}
              className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted transition-colors cursor-pointer text-muted-foreground hover:text-foreground"
              aria-label="Auto-refresh stopped. Click to resume."
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Auto-refresh stopped — click to resume
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  const progress = Math.min(elapsed / pollInterval, 1);
  const dashOffset = CIRCUMFERENCE * (1 - progress);
  const secondsLeft = Math.max(0, Math.ceil((pollInterval - elapsed) / 1000));

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            disabled={checking || cooldown}
            className="inline-flex items-center justify-center rounded-full p-0.5 hover:bg-muted transition-colors cursor-pointer disabled:cursor-default"
            aria-label={checking ? 'Checking sync status...' : `Next check in ${secondsLeft}s. Click to check now.`}
          >
            <svg
              width={SIZE}
              height={SIZE}
              viewBox={`0 0 ${SIZE} ${SIZE}`}
              className={checking ? 'animate-spin' : ''}
            >
              {/* Background circle */}
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={STROKE_WIDTH}
                className="text-muted-foreground/25"
              />
              {/* Progress arc */}
              <circle
                cx={SIZE / 2}
                cy={SIZE / 2}
                r={RADIUS}
                fill="none"
                stroke="currentColor"
                strokeWidth={STROKE_WIDTH}
                strokeLinecap="round"
                strokeDasharray={CIRCUMFERENCE}
                strokeDashoffset={dashOffset}
                className="text-blue-500"
                transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
              />
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent>
          {checking ? 'Checking...' : `Next check in ${secondsLeft}s — click to check now`}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
